import React, { useState, useEffect, useRef } from 'react';
import { Icons } from './Icons';
import { Server, FirewallRule, Service, FileItem } from '../types';

interface ServerDetailProps {
  server: Server;
  onBack: () => void;
  onUpdateRules: (serverId: string, newRules: FirewallRule[]) => void;
  authHeaders: any;
}

export const ServerDetail: React.FC<ServerDetailProps> = ({ server, onBack, onUpdateRules, authHeaders }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'services' | 'firewall' | 'files' | 'console'>('overview');
  
  // Firewall State
  const [rules, setRules] = useState<FirewallRule[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [newRule, setNewRule] = useState({ port: '', protocol: 'tcp', action: 'allow', source: '0.0.0.0/0' });
  
  // Services State
  const [services, setServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({ name: '', displayName: '' });

  // Resources State
  const [resources, setResources] = useState<Server['resources'] | null>(null);

  // File Manager State
  const [files, setFiles] = useState<FileItem[]>([]);
  const [currentPath, setCurrentPath] = useState('/');
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Code Editor State
  const [editorOpen, setEditorOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState<{name: string, path: string, content: string} | null>(null);
  const [savingFile, setSavingFile] = useState(false);

  // Console State
  const [consoleInput, setConsoleInput] = useState('');
  const prompt = `root@${server.name.replace(/\s+/g,'_').toLowerCase()}:~#`;
  const [logs, setLogs] = useState<string[]>([`Bağlantı kuruldu: ${server.ip}`, prompt]);
  const terminalEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeTab === 'services') fetchServices();
    if (activeTab === 'firewall') fetchFirewallRules();
    if (activeTab === 'overview') fetchResources();
    if (activeTab === 'files') fetchFiles(currentPath);
  }, [activeTab]);

  useEffect(() => {
    terminalEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs, activeTab]);

  const fetchWithAuth = async (body: any) => {
      return fetch('/api/backend.php', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({ ...body, ...authHeaders })
      });
  };

  const fetchResources = async () => {
    try {
        const res = await fetchWithAuth({ action: 'get_resources', server_id: server.id });
        const data = await res.json();
        if(data.success) setResources(data.resources);
    } catch(e) { console.error(e); }
  };

  const fetchFirewallRules = async () => {
    setLoadingRules(true);
    try {
        const res = await fetchWithAuth({ action: 'get_firewall_rules', server_id: server.id });
        const data = await res.json();
        if(data.success && data.rules) setRules(data.rules);
    } catch(e) { setLogs(p => [...p, "Firewall hatası."]); } 
    finally { setLoadingRules(false); }
  };

  const handleAddRule = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
        const res = await fetchWithAuth({ action: 'add_rule', server_id: server.id, rule: newRule });
        const data = await res.json();
        if (data.success) {
            fetchFirewallRules();
            setNewRule({...newRule, port: ''});
        } else { alert("Hata: " + data.message); }
    } catch (e) { alert("Bağlantı hatası."); }
  };

  const handleDeleteRule = async (ruleId: string) => {
      if(!confirm('Bu kuralı silmek istediğinize emin misiniz?')) return;
      try {
          const res = await fetchWithAuth({ action: 'delete_rule', server_id: server.id, rule_id: ruleId });
          const data = await res.json();
          if (data.success) fetchFirewallRules();
          else alert("Silinemedi: " + data.message);
      } catch(e) { alert("Hata"); }
  };

  const fetchServices = async () => {
    setLoadingServices(true);
    try {
        const res = await fetchWithAuth({ action: 'list_services', server_id: server.id });
        const data = await res.json();
        if (data.success) setServices(data.services);
    } catch(e) { console.error(e); }
    finally { setLoadingServices(false); }
  };

  const handleAddService = async (e: React.FormEvent) => {
      e.preventDefault();
      try {
          await fetchWithAuth({ action: 'add_service', server_id: server.id, service_name: newService.name, display_name: newService.displayName });
          setShowAddService(false); setNewService({name: '', displayName: ''}); fetchServices();
      } catch(e) { alert("Hata"); }
  };

  const handleServiceAction = async (serviceName: string, action: 'start' | 'stop' | 'restart') => {
      setServices(prev => prev.map(s => s.name === serviceName ? {...s, status: action === 'stop' ? 'stopped' : 'running'} : s));
      try { await fetchWithAuth({ action: 'service_action', server_id: server.id, service: serviceName, service_action: action }); setTimeout(fetchServices, 2000); } catch(e) { alert("Bağlantı hatası"); }
  };

  const fetchFiles = async (path: string) => {
      setLoadingFiles(true);
      try {
          const res = await fetchWithAuth({ action: 'file_manager', server_id: server.id, path: path });
          const data = await res.json();
          if(data.success) { setFiles(data.files); setCurrentPath(path); }
          else { alert("Dizin okunamadı: " + data.message); }
      } catch(e) { console.error(e); } finally { setLoadingFiles(false); }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);
      const reader = new FileReader();
      reader.onload = async () => {
          const base64Content = (reader.result as string).split(',')[1];
          try {
              const res = await fetchWithAuth({
                  action: 'upload_file',
                  server_id: server.id,
                  path: currentPath,
                  file: file.name,
                  content: base64Content
              });
              const data = await res.json();
              if(data.success) {
                  fetchFiles(currentPath);
              } else {
                  alert('Yükleme hatası: ' + (data.message || 'Bilinmeyen hata'));
              }
          } catch(e) { alert('Bağlantı hatası'); }
          finally { 
              setUploading(false); 
              if(fileInputRef.current) fileInputRef.current.value = ''; 
          }
      };
      reader.readAsDataURL(file);
  };

  const openFileEditor = async (fileName: string) => {
      const fullPath = currentPath === '/' ? '/' + fileName : currentPath + '/' + fileName;
      try {
          const res = await fetchWithAuth({ action: 'read_file', server_id: server.id, path: fullPath });
          const data = await res.json();
          if(data.success) {
              setCurrentFile({ name: fileName, path: fullPath, content: data.content });
              setEditorOpen(true);
          } else { alert("Dosya okunamadı: " + data.message); }
      } catch(e) { alert("Hata"); }
  };

  const saveFile = async () => {
      if(!currentFile) return;
      setSavingFile(true);
      try {
          const res = await fetchWithAuth({ action: 'save_file', server_id: server.id, path: currentFile.path, content: currentFile.content });
          const data = await res.json();
          if(data.success) {
              setEditorOpen(false);
              alert("Dosya kaydedildi.");
          } else { alert("Kaydedilemedi: " + data.message); }
      } catch(e) { alert("Hata"); } finally { setSavingFile(false); }
  };

  const handleConsoleCommand = async (e: React.FormEvent) => {
      e.preventDefault(); if (!consoleInput.trim()) return;
      const cmd = consoleInput; setLogs(prev => [...prev, `${prompt} ${cmd}`]); setConsoleInput('');
      try {
          const res = await fetchWithAuth({ action: 'terminal_cmd', server_id: server.id, command: cmd });
          const data = await res.json();
          if(data.output) setLogs(prev => [...prev, data.output]);
      } catch (err) { setLogs(prev => [...prev, 'Bağlantı hatası.']); }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-950">
      <header className="h-16 border-b border-gray-800 bg-gray-900 flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors">
            <Icons.LogOut className="w-5 h-5 rotate-180" />
          </button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              {server.name}
              <span className={`w-2.5 h-2.5 rounded-full ${server.status === 'online' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-red-500'}`}></span>
            </h1>
            <div className="text-xs text-gray-500 font-mono flex items-center gap-2">
                {server.ip} <span className="text-gray-700">|</span> {server.os}
            </div>
          </div>
        </div>
      </header>

      <div className="border-b border-gray-800 bg-gray-900 px-6 flex gap-6 overflow-x-auto shrink-0">
        {[
          { id: 'overview', label: 'Genel Bakış', icon: Icons.Activity },
          { id: 'services', label: 'Servisler', icon: Icons.Command },
          { id: 'firewall', label: 'Güvenlik Duvarı', icon: Icons.Shield },
          { id: 'files', label: 'Dosya Yöneticisi', icon: Icons.Folder },
          { id: 'console', label: 'Terminal', icon: Icons.Terminal },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'border-blue-500 text-white' : 'border-transparent text-gray-400 hover:text-gray-300'}`}>
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto bg-gray-950 p-6 relative">
        {activeTab === 'overview' && (
          <div className="flex flex-col h-full max-w-6xl mx-auto">
             {!resources ? (
                 <div className="flex items-center justify-center h-64 text-blue-400 gap-3">
                     <Icons.RefreshCw className="w-8 h-8 animate-spin"/>
                     <span className="text-lg">Sunucu verileri analiz ediliyor...</span>
                 </div>
             ) : (
             <div className="space-y-8 animate-in fade-in duration-500">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="bg-gray-900 border border-gray-800 p-8 rounded-2xl relative overflow-hidden group flex flex-col items-center justify-center text-center shadow-lg hover:border-blue-500/30 transition-all">
                        <div className="absolute top-4 right-4 text-xs font-mono text-gray-600 border border-gray-800 px-2 py-1 rounded">{resources.cpuModel}</div>
                        <div className="p-4 bg-blue-900/10 rounded-full mb-4"><Icons.Cpu className="w-10 h-10 text-blue-500" /></div>
                        <h3 className="text-gray-400 font-medium mb-1">CPU</h3>
                        <div className="text-5xl font-bold text-white mb-2">%{resources.cpu}</div>
                        <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden mt-4"><div className="h-full bg-blue-500 transition-all duration-1000" style={{width: `${resources.cpu}%`}}></div></div>
                    </div>
                    
                    <div className="bg-gray-900 border border-gray-800 p-8 rounded-2xl relative overflow-hidden group flex flex-col items-center justify-center text-center shadow-lg hover:border-purple-500/30 transition-all">
                        <div className="p-4 bg-purple-900/10 rounded-full mb-4"><Icons.HardDrive className="w-10 h-10 text-purple-500" /></div>
                        <h3 className="text-gray-400 font-medium mb-1">RAM</h3>
                        <div className="text-5xl font-bold text-white mb-2">%{resources.ram}</div>
                        <div className="text-xs text-gray-500 font-mono mt-1">{resources.ramUsed} / {resources.ramTotal}</div>
                        <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden mt-4"><div className="h-full bg-purple-500 transition-all duration-1000" style={{width: `${resources.ram}%`}}></div></div>
                    </div>

                    <div className="bg-gray-900 border border-gray-800 p-8 rounded-2xl relative overflow-hidden group flex flex-col items-center justify-center text-center shadow-lg hover:border-green-500/30 transition-all">
                        <div className="p-4 bg-green-900/10 rounded-full mb-4"><Icons.Database className="w-10 h-10 text-green-500" /></div>
                        <h3 className="text-gray-400 font-medium mb-1">Disk</h3>
                        <div className="text-5xl font-bold text-white mb-2">%{resources.disk}</div>
                        <div className="text-xs text-gray-500 font-mono mt-1">{resources.diskUsed} / {resources.diskTotal}</div>
                        <div className="w-full bg-gray-800 h-2 rounded-full overflow-hidden mt-4"><div className="h-full bg-green-500 transition-all duration-1000" style={{width: `${resources.disk}%`}}></div></div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><Icons.Shield className="w-5 h-5 text-gray-400"/> Sistem Bilgisi</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between border-b border-gray-800 pb-2"><span className="text-gray-500">Host Adı</span> <span className="text-gray-200">{server.name}</span></div>
                            <div className="flex justify-between border-b border-gray-800 pb-2"><span className="text-gray-500">IP Adresi</span> <span className="font-mono text-gray-200">{server.ip}</span></div>
                            <div className="flex justify-between border-b border-gray-800 pb-2"><span className="text-gray-500">İşletim Sistemi</span> <span className="text-gray-200">{server.os}</span></div>
                            <div className="flex justify-between border-b border-gray-800 pb-2"><span className="text-gray-500">Firewall</span> <span className="uppercase text-blue-400">{server.firewallType}</span></div>
                        </div>
                    </div>
                </div>
             </div>
             )}
          </div>
        )}

        {activeTab === 'firewall' && (
          <div className="flex flex-col lg:flex-row gap-6 h-full">
              <div className="flex-1 bg-gray-900 border border-gray-800 rounded-xl overflow-hidden flex flex-col">
                <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900">
                  <h3 className="font-bold flex items-center gap-2"><Icons.Shield className="w-5 h-5 text-green-500"/> Kural Tablosu ({server.firewallType})</h3>
                  <button onClick={fetchFirewallRules} className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white"><Icons.RefreshCw className={`w-4 h-4 ${loadingRules ? 'animate-spin':''}`} /></button>
                </div>
                <div className="flex-1 overflow-auto">
                    <table className="w-full text-left text-sm border-collapse">
                    <thead className="bg-gray-800 text-gray-400 sticky top-0 z-10">
                        <tr>
                            <th className="p-3 font-medium">No</th>
                            <th className="p-3 font-medium">Hedef (Action)</th>
                            <th className="p-3 font-medium">Protokol</th>
                            <th className="p-3 font-medium">Port / Servis</th>
                            <th className="p-3 font-medium">Kaynak (Source)</th>
                            <th className="p-3 font-medium">Detay</th>
                            <th className="p-3 font-medium text-right">İşlem</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800 text-gray-300">
                        {rules.length === 0 ? (
                            <tr><td colSpan={7} className="p-8 text-center text-gray-500">Kural bulunamadı veya yükleniyor...</td></tr>
                        ) : (
                            rules.map((rule, idx) => (
                            <tr key={idx} className="hover:bg-gray-800/50 transition-colors group">
                                <td className="p-3 font-mono text-gray-500 text-xs">{rule.id}</td>
                                <td className="p-3">
                                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider border ${
                                        rule.target === 'ACCEPT' || rule.action === 'allow' ? 'bg-green-900/20 text-green-400 border-green-900' : 
                                        rule.target === 'DROP' || rule.action === 'deny' ? 'bg-red-900/20 text-red-400 border-red-900' :
                                        'bg-gray-800 text-gray-300 border-gray-700'
                                    }`}>
                                        {rule.target || rule.action}
                                    </span>
                                </td>
                                <td className="p-3 uppercase text-xs font-medium">{rule.protocol}</td>
                                <td className="p-3 font-mono text-blue-300">{rule.port === 'any' ? '*' : rule.port}</td>
                                <td className="p-3 font-mono text-yellow-500/80">{rule.source}</td>
                                <td className="p-3 text-xs text-gray-500 truncate max-w-[150px]" title={rule.extra}>{rule.extra}</td>
                                <td className="p-3 text-right">
                                    <button onClick={() => handleDeleteRule(rule.id)} className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-900/20 rounded opacity-0 group-hover:opacity-100 transition-all" title="Kuralı Sil">
                                        <Icons.Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                            ))
                        )}
                    </tbody>
                    </table>
                </div>
              </div>

              <div className="w-full lg:w-80 bg-gray-900 border border-gray-800 rounded-xl p-5 h-fit shrink-0">
                <h3 className="font-bold mb-4 flex items-center gap-2 text-white"><Icons.Plus className="w-4 h-4 text-blue-500" /> Hızlı Kural Ekle</h3>
                <form onSubmit={handleAddRule} className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setNewRule({...newRule, action: 'allow'})} className={`py-2 rounded border text-xs font-bold transition-colors ${newRule.action === 'allow' ? 'bg-green-600 border-green-500 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'}`}>ALLOW</button>
                      <button type="button" onClick={() => setNewRule({...newRule, action: 'deny'})} className={`py-2 rounded border text-xs font-bold transition-colors ${newRule.action === 'deny' ? 'bg-red-600 border-red-500 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'}`}>DENY</button>
                  </div>
                  <div>
                      <label className="text-xs text-gray-500 mb-1 block">Protokol</label>
                      <select className="w-full bg-gray-950 border border-gray-700 rounded p-2 text-sm focus:border-blue-500 outline-none text-white" value={newRule.protocol} onChange={e => setNewRule({...newRule, protocol: e.target.value as any})}>
                          <option value="tcp">TCP</option>
                          <option value="udp">UDP</option>
                          <option value="icmp">ICMP (Ping)</option>
                      </select>
                  </div>
                  <div>
                      <label className="text-xs text-gray-500 mb-1 block">Port (Boş = Tümü)</label>
                      <input type="text" placeholder="80, 443 veya 8000:9000" className="w-full bg-gray-950 border border-gray-700 rounded p-2 text-sm focus:border-blue-500 outline-none font-mono text-white" value={newRule.port} onChange={e => setNewRule({...newRule, port: e.target.value})} />
                  </div>
                  <div>
                      <label className="text-xs text-gray-500 mb-1 block">Kaynak IP / CIDR</label>
                      <input type="text" className="w-full bg-gray-950 border border-gray-700 rounded p-2 text-sm focus:border-blue-500 outline-none font-mono text-white" value={newRule.source} onChange={e => setNewRule({...newRule, source: e.target.value})} />
                      <p className="text-[10px] text-gray-600 mt-1">Örn: 0.0.0.0/0 (Herkes) veya 192.168.1.5</p>
                  </div>
                  <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded font-medium transition-colors shadow-lg shadow-blue-900/20">Kuralı Uygula</button>
                </form>
              </div>
          </div>
        )}

        {activeTab === 'files' && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden flex flex-col h-full">
                <div className="p-4 border-b border-gray-800 flex items-center justify-between bg-gray-900 shrink-0">
                    <div className="flex items-center gap-2 text-sm font-mono text-gray-300 overflow-hidden">
                        <Icons.Folder className="w-5 h-5 text-yellow-500 shrink-0" />
                        <span className="truncate">{currentPath}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />
                        <button onClick={() => fileInputRef.current?.click()} className="p-2 hover:bg-gray-800 rounded text-gray-400 hover:text-white flex items-center gap-1.5 text-xs border border-gray-800 hover:border-gray-700 transition-colors" disabled={uploading}>
                            {uploading ? <Icons.RefreshCw className="w-4 h-4 animate-spin"/> : <Icons.Upload className="w-4 h-4" />}
                            <span className="hidden sm:inline">Yükle</span>
                        </button>
                        <div className="w-px h-6 bg-gray-800 mx-1"></div>
                        <button onClick={() => { const p = currentPath.split('/'); p.pop(); fetchFiles(p.length<=1?'/':p.join('/')); }} disabled={currentPath === '/'} className="p-2 hover:bg-gray-800 rounded text-gray-400 hover:text-white disabled:opacity-30"><Icons.ArrowLeft className="w-5 h-5" /></button>
                        <button onClick={() => fetchFiles(currentPath)} className="p-2 hover:bg-gray-800 rounded text-gray-400 hover:text-white"><Icons.RefreshCw className={`w-4 h-4 ${loadingFiles ? 'animate-spin':''}`} /></button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto p-2">
                    {files.length === 0 ? ( <div className="text-center py-10 text-gray-500">Bu klasör boş.</div> ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                            {files.map((file, idx) => (
                                <div key={idx} className={`group p-3 rounded border border-transparent hover:bg-gray-800 hover:border-gray-700 flex items-center justify-between cursor-pointer transition-all ${file.type === 'folder' ? 'text-white' : 'text-gray-400'}`}>
                                    <div className="flex items-center gap-3 overflow-hidden" onClick={() => file.type === 'folder' ? fetchFiles(currentPath === '/' ? '/'+file.name : currentPath+'/'+file.name) : null}>
                                        {file.type === 'folder' ? <Icons.Folder className="w-8 h-8 text-yellow-500 shrink-0" /> : <Icons.FileText className="w-8 h-8 text-blue-400 shrink-0" />}
                                        <div className="overflow-hidden">
                                            <div className="truncate font-medium text-sm">{file.name}</div>
                                            <div className="text-xs text-gray-600 flex gap-2"><span>{file.size}</span><span>{file.permissions}</span></div>
                                        </div>
                                    </div>
                                    {file.type === 'file' && (
                                        <button onClick={() => openFileEditor(file.name)} className="p-2 text-gray-600 hover:text-blue-400 hover:bg-blue-900/20 rounded opacity-0 group-hover:opacity-100 transition-all"><Icons.Edit className="w-4 h-4"/></button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )}
        
        {/* Code Editor Modal */}
        {editorOpen && currentFile && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                <div className="bg-gray-900 border border-gray-800 w-full max-w-4xl h-[80vh] flex flex-col rounded-xl shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-gray-900">
                        <div className="flex items-center gap-3">
                            <Icons.FileCode className="w-5 h-5 text-blue-500"/>
                            <span className="font-mono text-sm text-gray-300">{currentFile.path}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={saveFile} disabled={savingFile} className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors">
                                {savingFile ? <Icons.RefreshCw className="w-4 h-4 animate-spin"/> : <Icons.Save className="w-4 h-4"/>} Kaydet
                            </button>
                            <button onClick={() => setEditorOpen(false)} className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded"><Icons.X className="w-5 h-5"/></button>
                        </div>
                    </div>
                    <div className="flex-1 relative">
                        <textarea 
                            className="w-full h-full bg-[#0d1117] text-gray-300 font-mono text-sm p-4 resize-none outline-none border-none leading-relaxed"
                            value={currentFile.content}
                            onChange={(e) => setCurrentFile({...currentFile, content: e.target.value})}
                            spellCheck={false}
                        />
                    </div>
                </div>
            </div>
        )}

        {/* Console Tab - (Already defined above but keep it cleaner here if needed) */}
        {activeTab === 'console' && (
          <div className="h-full bg-black border border-gray-800 rounded-xl p-4 font-mono text-sm overflow-hidden flex flex-col shadow-inner">
             <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-800">
                <span className="text-xs text-gray-500">SSH/System Shell</span>
                <button onClick={() => setLogs([])} className="text-xs text-gray-400 hover:text-white px-2 py-1 bg-gray-800 rounded">Temizle</button>
             </div>
             <div className="flex-1 overflow-auto space-y-1 pb-4">
                {logs.map((log, i) => <div key={i} className="text-gray-300 break-all whitespace-pre-wrap">{log}</div>)}
                <div ref={terminalEndRef} />
             </div>
             <form onSubmit={handleConsoleCommand} className="border-t border-gray-800 pt-2 flex items-center gap-2">
                <span className="text-green-500">{server.firewallType === 'windows' ? 'PS>' : prompt}</span>
                <input className="bg-transparent border-none outline-none text-white w-full focus:ring-0" placeholder="Komut girin..." value={consoleInput} onChange={e => setConsoleInput(e.target.value)} />
             </form>
          </div>
        )}

        {activeTab === 'services' && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-gray-800 flex justify-between items-center">
                   <h3 className="font-bold flex items-center gap-2"><Icons.Command className="w-5 h-5 text-gray-400"/> Servis Listesi</h3>
                   <div className="flex gap-2">
                       <button onClick={() => setShowAddService(true)} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm flex items-center gap-2"><Icons.Plus className="w-4 h-4" /> Servis Ekle</button>
                       <button onClick={fetchServices} className="p-2 bg-gray-800 hover:bg-gray-700 rounded"><Icons.RefreshCw className={`w-4 h-4 ${loadingServices ? 'animate-spin' : ''}`} /></button>
                   </div>
                </div>
                <div className="divide-y divide-gray-800">
                    {services.map(svc => (
                        <div key={svc.name} className="p-4 flex items-center justify-between hover:bg-gray-800/30 transition-colors group">
                            <div className="flex items-center gap-4">
                                <div className={`p-2 rounded-lg ${svc.status === 'running' ? 'bg-green-900/20 text-green-500' : 'bg-red-900/20 text-red-500'}`}><Icons.Activity className="w-5 h-5" /></div>
                                <div><h4 className="font-bold text-white flex items-center gap-2">{svc.displayName} {(svc as any).isCustom && <span className="text-[10px] bg-gray-700 px-1 rounded text-gray-300">USER</span>}</h4><span className="text-xs text-gray-500 font-mono">{svc.name}</span></div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-xs font-bold uppercase mr-4 ${svc.status === 'running' ? 'text-green-400' : 'text-red-400'}`}>{svc.status === 'running' ? 'ÇALIŞIYOR' : 'DURDU'}</span>
                                {svc.status === 'stopped' ? (
                                    <button onClick={() => handleServiceAction(svc.name, 'start')} className="p-2 text-green-400 hover:bg-green-900/20 rounded"><Icons.PlayCircle className="w-5 h-5" /></button>
                                ) : (
                                    <button onClick={() => handleServiceAction(svc.name, 'stop')} className="p-2 text-red-400 hover:bg-red-900/20 rounded"><Icons.StopCircle className="w-5 h-5" /></button>
                                )}
                                <button onClick={() => handleServiceAction(svc.name, 'restart')} className="p-2 text-blue-400 hover:bg-blue-900/20 rounded"><Icons.RefreshCw className="w-5 h-5" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}
      </div>
    </div>
  );
};